/**
 * Utility functions for generating canonical URLs
 */

const DOMAIN = "https://htu.edu.jo";

/**
 * Generates a canonical URL by removing language prefixes and normalizing the path
 * @param pathname - The current pathname
 * @param lang - The current language (optional, for context)
 * @returns The canonical URL (always English version)
 */
export function getCanonicalUrl(pathname: string, lang?: string): string {
  // Remove language prefix for canonical URL
  let cleanPath = pathname;
  
  // Handle Arabic paths
  if (pathname.startsWith('/ar/')) {
    cleanPath = pathname.replace('/ar', '');
  } else if (pathname === '/ar') {
    cleanPath = '/';
  }
  
  // Ensure path starts with /
  if (cleanPath && !cleanPath.startsWith('/')) {
    cleanPath = '/' + cleanPath;
  }
  
  // Handle empty or root path
  if (!cleanPath || cleanPath === '') {
    cleanPath = '/';
  }
  
  return `${DOMAIN}${cleanPath}`;
}

/**
 * Generates canonical URL for dynamic routes with unique URLs
 * @param routeType - The type of route (news, newsletter, profile, etc.)
 * @param uniqueUrl - The unique URL slug
 * @returns The canonical URL
 */
export function getCanonicalUrlForDynamicRoute(
  routeType: string, 
  uniqueUrl: string
): string {
  // Ensure routeType doesn't start with /
  const cleanRouteType = routeType.startsWith('/') ? routeType.slice(1) : routeType;
  
  return `${DOMAIN}/${cleanRouteType}/${uniqueUrl}`;
}

/**
 * Generates canonical URL from Next.js params and pathname
 * Useful for page components that have access to params
 * @param params - Next.js params object
 * @param pathname - Current pathname (optional)
 * @returns The canonical URL
 */
export function getCanonicalFromParams(
  params: { lang: string; [key: string]: string | string[] },
  pathname?: string
): string {
  if (pathname) {
    return getCanonicalUrl(pathname, params.lang);
  }
  
  // If no pathname provided, construct from params
  const { lang, ...otherParams } = params;
  
  // Build path from params
  let path = '';
  Object.entries(otherParams).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      path += '/' + value.join('/');
    } else {
      path += '/' + value;
    }
  });
  
  return getCanonicalUrl(path, lang);
}

/**
 * Gets the current pathname from window.location (client-side only)
 * @returns Current pathname or empty string if not available
 */
export function getCurrentPathname(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location.pathname;
}
